import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiTrendingDown, FiCheckCircle, FiActivity, FiTarget } = FiIcons;

const InterventionEffectTracking = ({ interventions = [] }) => {
  const calculateEffect = (before, after, inverse = false) => {
    const diff = after - before;
    const percentage = ((diff / before) * 100).toFixed(1);
    const isPositive = inverse ? diff < 0 : diff > 0;
    return { percentage, isPositive, diff };
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      <div className="p-6 border-bottom border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <SafeIcon icon={FiActivity} className="text-indigo-600 dark:text-indigo-400 w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Intervention Effect Tracking</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Sammenligning av KPIer før og etter tiltak</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full flex items-center gap-1">
            <SafeIcon icon={FiCheckCircle} className="w-3 h-3" />
            AI-verifisert effekt
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/30">
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tiltak & Dato</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Org. Helse (OHS)</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Psyk. Trygghet</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Stress-indeks</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Konfliktrisiko</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {interventions.map((item, idx) => (
              <motion.tr 
                key={item.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{item.intervention_name}</span>
                    <span className="text-xs text-slate-500">{item.completion_date}</span>
                  </div>
                </td>
                
                {[
                  { key: 'ohs', label: 'OHS', inverse: false },
                  { key: 'psych_safety', label: 'PS', inverse: false },
                  { key: 'stress_index', label: 'Stress', inverse: true },
                  { key: 'conflict_risk', label: 'Konflikt', inverse: true }
                ].map((metric) => {
                  const effect = calculateEffect(item.metrics_before[metric.key], item.metrics_after[metric.key], metric.inverse);
                  return (
                    <td key={metric.key} className="px-6 py-4 text-center">
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-slate-400 line-through">{item.metrics_before[metric.key]}</span>
                          <SafeIcon icon={FiIcons.FiArrowRight} className="w-3 h-3 text-slate-300" />
                          <span className="text-sm font-bold text-slate-900 dark:text-white">{item.metrics_after[metric.key]}</span>
                        </div>
                        <div className={`flex items-center gap-1 text-[10px] font-bold uppercase ${effect.isPositive ? 'text-green-600 dark:text-green-400' : 'text-rose-600 dark:text-rose-400'}`}>
                          <SafeIcon icon={effect.isPositive ? FiTrendingUp : FiTrendingDown} className="w-3 h-3" />
                          {Math.abs(effect.percentage)}%
                        </div>
                      </div>
                    </td>
                  );
                })}

                <td className="px-6 py-4 text-right">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    Fullført
                  </span>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InterventionEffectTracking;